<template>
  <v-container>
    <div v-if="isLoading" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-row v-if="!isLoading" justify="center">
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title class="headline">Regenerate Homeportal2 Brandings</v-card-title>
          <v-card-text>
            <v-row no-gutters align="center">
              <v-col cols="12" md="8" class="py-0">
                <div class="text-subtitle-1 mb-2">
                  Active Homeportal2 brandings:
                  <span v-if="isLoadingActiveBranding" style="color: #1976D2;">
                    <v-progress-circular indeterminate color="primary" size="20" width="2"></v-progress-circular>
                  </span>
                  <span v-else class="font-weight-bold" style="color: #1976D2;">{{ activeBrandingCount }}</span>
                </div>

                <v-select
                    :items="branches"
                    v-model="release.branch"
                    label="Select a branch"
                    placeholder="Select a branch to generate branding for"
                    item-text="name"
                    item-value="name"
                    :disabled="isLoadingActiveBranding"
                />
              </v-col>

              <v-col cols="12" md="4" class="pl-md-3">
                <v-btn
                    color="primary"
                    :loading="isRegenerating"
                    @click="openConfirmationDialog"
                    :disabled="!release.branch || isRegenerating || isLoadingActiveBranding"
                    block
                >
                  Regenerate all
                </v-btn>
              </v-col>
            </v-row>

            <v-row v-if="isRegenerating" justify="center" class="mt-4">
              <v-col cols="12">
                <div class="text-center">
                  Processing
                  <span class="font-weight-bold" style="color: #1976D2;">{{ currentTenant }}</span>
                  (<span class="font-weight-bold" style="color: #1976D2;">{{ currentProgress }}</span> of
                  <span class="font-weight-bold" style="color: #1976D2;">{{ totalTenantsToProcess }}</span>)
                </div>
                <v-progress-linear
                    :value="progressValue"
                    color="primary"
                    height="10"
                    rounded
                ></v-progress-linear>
              </v-col>
            </v-row>

            <v-row v-if="jobStatus && !isRegenerating" class="mt-4">
              <v-col cols="12">
                <div class="text-subtitle-2">
                  Last job status:
                  <span class="font-weight-bold" style="color: #1976D2;">{{ jobStatus.status }}</span>
                </div>
                <div>
                  Processed {{ jobStatus.processed_tenants }} / {{ jobStatus.total_tenants }},
                  successes {{ jobStatus.success_count }},
                  failures {{ jobStatus.failure_count }}
                </div>
                <div v-if="jobStatus.failures && jobStatus.failures.length" class="mt-2">
                  Failed tenants:
                  <span class="font-weight-bold" style="color: #1976D2;">
                    {{ jobStatus.failures.map(f => f.tenant_name).join(', ') }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="showConfirmationDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>
          Are you sure you want to regenerate all
          <span class="font-weight-bold" style="color: #1976D2;">{{ activeBrandingCount }}</span>
          Homeportal2 brandings? This action may take some time.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showConfirmationDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" text @click="regenerateAll">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { http, apiUrl } from '@/services/http';

export default {
  name: 'ToolsRegenerateBrandings',
  components: {},
  data: () => ({
    branches: [],
    tenants: [],
    activeTenantsList: [],
    tenantContextId: null,
    release: {
      app: 'coursepro',
      branch: null,
    },
    isRegenerating: false,
    activeJobId: null,
    jobStatus: null,
    currentProgress: 0,
    totalTenantsToProcess: 0,
    activeBrandingCount: 0,
    currentTenant: '',
    isLoading: true,
    isLoadingActiveBranding: false,
    snackbar: {
      show: false,
      text: '',
      color: 'success',
    },
    showConfirmationDialog: false,
    statusPollTimer: null,
    statusPollInFlight: false,
  }),
  async created() {
    try {
      const branchesResponse = await this.getBranches();
      this.branches = branchesResponse.data;
      this.isLoading = false;
      this.isLoadingActiveBranding = true;

      await this.loadActiveBrandings();
      await this.resumeLatestJob();

    } catch (error) {
      this.showSnackbar('Failed to load initial page data.', 'error');
      console.error("Error during created hook:", error);
    } finally {
      this.isLoadingActiveBranding = false;
      this.isLoading = false;
    }
  },
  beforeDestroy() {
    this.stopStatusPolling();
  },
  computed: {
    progressValue() {
      if (!this.totalTenantsToProcess) {
        return 0;
      }

      return (this.currentProgress / this.totalTenantsToProcess) * 100;
    }
  },
  methods: {
    async loadActiveBrandings() {
      const [activeBrandingResponse, tenantStatsResponse] = await Promise.all([
        http.get(`${apiUrl}/tenant/homeportal2/brandings/active-tenants`),
        http.get(`${apiUrl}/dashboard/stats/tenants`),
      ]);

      this.activeTenantsList = activeBrandingResponse.data.tenants;
      this.activeBrandingCount = this.activeTenantsList.length;
      this.tenantContextId = this.resolveTenantContextId(this.activeTenantsList, tenantStatsResponse.data.data);
    },
    async resumeLatestJob() {
      if (!this.tenantContextId) {
        this.updateJobState(null);
        return;
      }

      try {
        const response = await http.get(`${this.getRegenerationJobsEndpoint()}/latest`);
        const job = response.data.job;

        if (!job) {
          this.updateJobState(null);
          return;
        }

        this.updateJobState(job);

        if (job.status === 'queued' || job.status === 'running') {
          this.startStatusPolling();
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.updateJobState(null);
          return;
        }

        throw error;
      }
    },
    openConfirmationDialog() {
      this.showConfirmationDialog = true;
    },
    async getBranches() {
      const params = {app: this.release.app};
      return http.get(`${apiUrl}/repos/branches`, {params});
    },
    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    resolveTenantContextId(activeTenants, tenantStats) {
      if (!Array.isArray(tenantStats) || !tenantStats.length) {
        return null;
      }

      const tenantIdsByName = new Map(
        tenantStats
          .filter((tenant) => tenant && tenant.tenant_name && tenant.id)
          .map((tenant) => [tenant.tenant_name, String(tenant.id)])
      );

      const sorted = [...activeTenants].sort();
      for (const tenantName of sorted) {
        if (tenantIdsByName.has(tenantName)) {
          return tenantIdsByName.get(tenantName);
        }
      }

      return null;
    },
    getRegenerationJobsEndpoint() {
      if (!this.tenantContextId) {
        throw new Error('Tenant context is unavailable for regeneration jobs.');
      }

      return `${apiUrl}/tenant/${this.tenantContextId}/homeportal2/branding/regenerate-jobs`;
    },
    updateJobState(job) {
      this.jobStatus = job;
      this.activeJobId = job ? job.id : null;
      this.isRegenerating = Boolean(job && (job.status === 'queued' || job.status === 'running'));
      this.currentProgress = job ? job.processed_tenants : 0;
      this.totalTenantsToProcess = job ? job.total_tenants : 0;
      this.currentTenant = job ? (job.current_tenant || '') : '';
    },
    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },
    startStatusPolling() {
      this.stopStatusPolling();
      this.statusPollTimer = setInterval(async () => {
        await this.fetchJobStatus();
      }, 3000);
    },
    async fetchJobStatus() {
      if (!this.activeJobId) {
        this.stopStatusPolling();
        return;
      }

      if (this.statusPollInFlight) {
        return;
      }

      this.statusPollInFlight = true;

      try {
        let response;
        try {
          response = await http.get(`${this.getRegenerationJobsEndpoint()}/${this.activeJobId}`);
        } catch (transportError) {
          console.error('Failed to poll branding regeneration job:', transportError);
          this.stopStatusPolling();
          const currentJob = this.jobStatus || {};
          this.updateJobState({
            id: this.activeJobId,
            status: 'paused',
            processed_tenants: currentJob.processed_tenants ?? this.currentProgress ?? 0,
            total_tenants: currentJob.total_tenants ?? this.totalTenantsToProcess ?? 0,
            success_count: currentJob.success_count ?? 0,
            failure_count: currentJob.failure_count ?? 0,
            current_tenant: currentJob.current_tenant ?? this.currentTenant ?? '',
            failures: currentJob.failures || [],
            error: 'Unable to refresh branding regeneration status.',
          });
          this.activeJobId = null;
          this.showSnackbar('Unable to refresh branding regeneration status. Polling paused.', 'warning');
          return;
        }

        const job = response.data.job;
        if (!job) {
          return;
        }

        this.updateJobState(job);

        if (job.status === 'complete' || job.status === 'failed') {
          this.stopStatusPolling();

          try {
            await this.loadActiveBrandings();
          } catch (loadError) {
            console.error('Failed to refresh active brandings after job completion:', loadError);
            this.showSnackbar('Regeneration finished, but active branding list could not be refreshed.', 'warning');
          }

          if (job.status === 'complete') {
            const message = job.failure_count
              ? `Branding regeneration completed with ${job.failure_count} tenant failures.`
              : 'Branding regeneration completed successfully.';
            this.showSnackbar(message, job.failure_count ? 'warning' : 'success');
          } else {
            this.showSnackbar('Branding regeneration job failed.', 'error');
          }
        }
      } catch (error) {
        console.error('Unexpected error while processing branding regeneration status:', error);
        this.stopStatusPolling();
        const currentJob = this.jobStatus || {};
        this.updateJobState({
          id: this.activeJobId,
          status: 'error',
          processed_tenants: currentJob.processed_tenants ?? this.currentProgress ?? 0,
          total_tenants: currentJob.total_tenants ?? this.totalTenantsToProcess ?? 0,
          success_count: currentJob.success_count ?? 0,
          failure_count: currentJob.failure_count ?? 0,
          current_tenant: currentJob.current_tenant ?? this.currentTenant ?? '',
          failures: currentJob.failures || [],
          error: error.message || 'Unexpected polling error',
        });
        this.activeJobId = null;
        this.showSnackbar('Branding regeneration status polling failed. Please retry.', 'error');
      } finally {
        this.statusPollInFlight = false;
      }
    },
    async regenerateAll() {
      this.showConfirmationDialog = false;

      if (!this.release.branch) {
        this.showSnackbar('Please select a branch first', 'error');
        return;
      }

      if (!this.tenantContextId) {
        this.showSnackbar('Tenant context is unavailable. Please refresh the page.', 'error');
        return;
      }

      try {
        if (this.activeBrandingCount === 0) {
          this.showSnackbar('No active brandings to regenerate.', 'info');
          return;
        }

        const payload = {
          app: this.release.app,
          branch: this.release.branch,
        };
        const response = await http.post(this.getRegenerationJobsEndpoint(), payload);
        const job = response.data.job;

        this.updateJobState(job);
        this.showSnackbar('Branding regeneration started in the background.', 'success');
        this.startStatusPolling();
      } catch (error) {
        console.error("Error starting branding regeneration:", error);
        if (error.response && error.response.status === 409 && error.response.data.job) {
          this.updateJobState(error.response.data.job);
          this.startStatusPolling();
          this.showSnackbar('A branding regeneration job is already running. Resuming status.', 'warning');
          return;
        }
        this.showSnackbar('Failed to start branding regeneration. Please try again.', 'error');
      }
    }
  }
}
</script>

<style>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}
</style>
